export type PaymentOrderType = 'robot' | 'assistant' | 'retention' | 'point' | 'quota-addon';
export type PaymentResultType = 'success' | 'failed';

export interface PaymentReturnUrlPayload {
  orderType: PaymentOrderType;
  paymentResult: PaymentResultType;
  tenantId?: string | null;
  aipoolTenantId?: string | null;
  orderId?: string | null;
  targetId?: string | null;
  sourcePath?: string | null;
}

export interface PaymentSessionSnapshot {
  orderType: PaymentOrderType;
  tenantId?: string;
  aipoolTenantId?: string;
  orderId?: string;
  targetId?: string;
  paymentUrl?: string;
  reserveTimeoutSeconds?: number;
  payStartTime?: number;
  payExpireTime?: number;
  orderStatus?: string | number;
  paymentStatus?: string | number;
  payRequired?: boolean;
}

const PAYMENT_RESULT_ROUTE = '/payment-result';
const PAYMENT_SESSION_PREFIX = 'payment-result:';

export function buildPaymentResultUrl(payload: PaymentReturnUrlPayload): string {
  const url = new URL(PAYMENT_RESULT_ROUTE, window.location.origin);
  url.searchParams.set('paymentResult', payload.paymentResult);
  url.searchParams.set('orderType', payload.orderType);

  const tenantId = normalizeString(payload.tenantId);
  const aipoolTenantId = normalizeString(payload.aipoolTenantId);
  const orderId = normalizeString(payload.orderId);
  const targetId = normalizeString(payload.targetId);
  const sourcePath = normalizeString(payload.sourcePath);

  if (tenantId) url.searchParams.set('tenantId', tenantId);
  if (aipoolTenantId) url.searchParams.set('aipoolTenantId', aipoolTenantId);
  if (orderId) url.searchParams.set('orderId', orderId);
  if (targetId) url.searchParams.set('targetId', targetId);
  if (sourcePath) url.searchParams.set('sourcePath', sourcePath);

  return url.toString();
}

export function storePaymentSessionSnapshot(snapshot: PaymentSessionSnapshot): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  const orderId = normalizeString(snapshot.orderId);
  if (!orderId) return;
  window.sessionStorage.setItem(
    getPaymentSessionKey(snapshot.orderType, orderId),
    JSON.stringify(normalizePaymentSessionSnapshot(snapshot))
  );
}

export function readPaymentSessionSnapshot(orderType: PaymentOrderType, orderId?: string | null): PaymentSessionSnapshot | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  const normalizedOrderId = normalizeString(orderId);
  if (!normalizedOrderId) return null;
  const raw = window.sessionStorage.getItem(getPaymentSessionKey(orderType, normalizedOrderId));
  if (!raw) return null;
  try {
    return normalizePaymentSessionSnapshot(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function resolvePaymentExpireTime(raw?: any): number | null {
  const payExpireTime = toPositiveInteger(raw?.payExpireTime);
  if (payExpireTime) return payExpireTime;

  const payStartTime = toPositiveInteger(raw?.payStartTime);
  const reserveTimeoutSeconds = toPositiveInteger(raw?.reserveTimeoutSeconds);
  if (payStartTime && reserveTimeoutSeconds) {
    return payStartTime + reserveTimeoutSeconds * 1000;
  }
  return null;
}

export function getPaymentRemainingMs(raw?: any, now: number = Date.now()): number | null {
  const expireTime = resolvePaymentExpireTime(raw);
  if (!expireTime) return null;
  return Math.max(0, expireTime - now);
}

export function formatCountdown(ms?: number | null): string {
  if (ms === null || ms === undefined) return '';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
  }
  return `${pad2(minutes)}:${pad2(seconds)}`;
}

function normalizePaymentSessionSnapshot(snapshot: PaymentSessionSnapshot): PaymentSessionSnapshot {
  return {
    orderType: snapshot.orderType,
    tenantId: normalizeString(snapshot.tenantId) || undefined,
    aipoolTenantId: normalizeString(snapshot.aipoolTenantId) || undefined,
    orderId: normalizeString(snapshot.orderId) || undefined,
    targetId: normalizeString(snapshot.targetId) || undefined,
    paymentUrl: normalizeString(snapshot.paymentUrl) || undefined,
    reserveTimeoutSeconds: toPositiveInteger(snapshot.reserveTimeoutSeconds) || undefined,
    payStartTime: toPositiveInteger(snapshot.payStartTime) || undefined,
    payExpireTime: toPositiveInteger(snapshot.payExpireTime) || undefined,
    orderStatus: snapshot.orderStatus,
    paymentStatus: snapshot.paymentStatus,
    payRequired: snapshot.payRequired === true
  };
}

function getPaymentSessionKey(orderType: PaymentOrderType, orderId: string): string {
  return `${PAYMENT_SESSION_PREFIX}${orderType}:${orderId}`;
}

function normalizeString(value: unknown): string {
  return String(value ?? '').trim();
}

function toPositiveInteger(value: unknown): number | null {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}
